import React, { useState, useMemo } from "react";
import {
    Box,
    Paper,
    Typography,
    MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";

import { FEEDBACK_FIELDS } from "../../constants/feedBackFields";
import { validateForm } from "../../validators/validateForm";
import { sanitizeField } from "../../validators/sanitizeField";
import { validateField } from "../../validators/validateField"

import CustomInput from "../../components/common/CustomInput";
import CustomButton from "../../components/common/CustomButton";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomHeader from "../../components/common/CustomHeader";
import CustomCard from "../../components/common/CustomCard";
import { feedbackApi } from "../../api/feedbackApi"; // centralized API file
import { DotsLoader } from "../../components/common/spinLoader";

const FeedbackPage = () => {
    const theme = useTheme();

    // ---------- Constants ----------
    const FEEDBACK_TYPES = useMemo(
        () => ["Improvement", "Issue", "New Feature Request"],
        []
    );

    const MODULE_FEATURES = useMemo(
        () => [
            "Invitation Mail Send",
            "Question Generation",
            "Interview Scheduling",
            "CV Matching",
            "Candidate Assessment",
            "Interview Analysis",
            "Other",
        ],
        []
    );

    // ---------- States ----------
    const [formData, setFormData] = useState({
        feedbackType: "",
        featureName: "",
        comments: "",
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const markTouched = (field) => {
        setTouched((prev) => ({
            ...prev,
            [field]: true,
        }));
    };

    // ---------- Handlers ----------
    const handleCloseSnackbar = () =>
        setSnackbar((prev) => ({ ...prev, open: false }));

    const handleChange = (e) => {
        const { name, value } = e.target;
        const config = FEEDBACK_FIELDS[name];
        const sanitizedValue = sanitizeField(value, config?.type);

        setFormData((prev) => ({
            ...prev,
            [name]: sanitizedValue,
        }));

        if (!String(sanitizedValue).trim()) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
            return;
        }

        const error = validateField({
            ...config,
            value: sanitizedValue,
        });

        setErrors((prev) => ({
            ...prev,
            [name]: error,
        }));
    };

    const validate = () => {

        const {
            isValid,
            errors: validationErrors,
        } = validateForm(
            formData,
            FEEDBACK_FIELDS
        );

        setErrors(validationErrors);

        return {
            isValid,
            validationErrors,
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const {
            isValid,
            validationErrors,
        } = validate();

        if (!isValid) {

            setTouched(
                Object.keys(
                    FEEDBACK_FIELDS
                ).reduce(
                    (acc, field) => {
                        acc[field] = true;
                        return acc;
                    },
                    {}
                )
            );

            setErrors(
                validationErrors
            );

            setSnackbar({
                open: true,
                message:
                    "Please correct the validation errors.",
                severity: "warning",
            });

            return;
        }

        setLoading(true);
        try {
            const payload = {
                feedback_type: formData.feedbackType,
                feature_name: formData.featureName,
                comments: formData.comments,
            };

            const response = await feedbackApi.submitFeedback(payload);
            const { status, message } = response || {};

            if (status === 0) {
                setSnackbar({
                    open: true,
                    message: "🌐 Network error. Please check your internet connection.",
                    severity: "warning",
                });
                return;
            }

            if (status >= 500) {
                setSnackbar({
                    open: true,
                    message: message || "❌ Server error while submitting feedback.",
                    severity: "error",
                });
                return;
            }

            if (status >= 400) {
                setSnackbar({
                    open: true,
                    message: message || "❌ Failed to submit feedback. Please check your input.",
                    severity: "error",
                });
                return;
            }

            if (status === 200 || status === 201) {
                setFormData({
                    feedbackType: "",
                    featureName: "",
                    comments: "",
                });
                setSnackbar({
                    open: true,
                    message: message || "Thank you for your valuable feedback! 🙏",
                    severity: "success",
                });
                return;
            }

            setSnackbar({
                open: true,
                message: "⚠️ Unexpected response. Please try again.",
                severity: "warning",
            });

        } catch (error) {
            console.error("❌ Feedback submission failed:", error);
            setSnackbar({
                open: true,
                message: "❌ Something went wrong while submitting feedback.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const textFieldStyle = (theme) => ({
        mb: 2.5,
        "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            backgroundColor: theme.palette.background.paper,
            transition: "all 0.2s ease-in-out",
        },

        "& .MuiInputBase-input, & .MuiInputBase-inputMultiline, & .MuiSelect-select": {
            color: theme.palette.text.primary,
            fontWeight: 500,
            fontSize: "0.88rem",
        },

        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary,
            fontWeight: 500,
            fontSize: "0.88rem",
        },

        "& .MuiInputLabel-root.Mui-focused": {
            color: `${theme.palette.primary.main} !important`,
            fontWeight: 600,
        },

        "& .MuiInputLabel-shrink": {
            backgroundColor: `${theme.palette.background.paper} !important`,
            padding: "0 4px",
        },

        "& .MuiMenu-paper": {
            backgroundColor: `${theme.palette.background.paper} !important`,
            borderRadius: "10px",
            boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
            "& .MuiMenuItem-root": {
                color: theme.palette.text.primary,
                fontSize: "0.88rem",
                py: 1.2,
                "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                },
                "&.Mui-selected": {
                    backgroundColor: theme.palette.action.selected,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    "&:hover": {
                        backgroundColor: theme.palette.action.selected,
                    }
                }
            }
        }
    });

    // ---------- JSX ----------
    return (
        <Box
            display="flex"
            flexDirection="column"
            sx={{
                // minHeight: "100vh",
                width: "100%", // Explicitly take full width of parent
                backgroundColor: (theme) => theme.palette.background.default
            }}
        >
            <CustomHeader
                title="Share Feedback"
                subtitle="Help us improve by sharing your thoughts, issues, and feature requests."
                icon={<RateReviewOutlinedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />}
                breadcrumbs={[
                    { label: "Dashboard", path: "/recruiter/dashboard" },
                    { label: "Feedback", path: "/feedback" }
                ]}
            />

            {/* Centering Wrapper */}
            <Box
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",     // Vertically center
                    justifyContent: "center", // Horizontally center
                    width: "100%",            // Critical for horizontal centering
                    // slight bottom padding so it sits a bit higher visually
                }}
            >
                <CustomCard
                    sx={{
                        mx: "auto",
                        p: 1,
                        borderRadius: 2,
                        width: "100%",
                        maxWidth: 600,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                >
                    {/* Header */}
                    <Box sx={{ textAlign: "center", mb: 3, pt: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                            We'd Love to Hear From You
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Let us know what's working and what we can improve.
                        </Typography>
                    </Box>

                    <form onSubmit={handleSubmit}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                                gap: 2,
                                p: 1,
                            }}
                        >
                            {/* Feedback Type */}
                            <Box>
                                <CustomInput
                                    select
                                    name="feedbackType"
                                    label="Feedback Type"
                                    value={formData.feedbackType}
                                    size="small"
                                    onChange={handleChange}
                                    onBlur={() => markTouched("feedbackType")}
                                    required
                                    fullWidth
                                    error={touched.feedbackType && Boolean(errors.feedbackType)}
                                    helperText={(touched.feedbackType && errors.feedbackType) || " "}
                                    SelectProps={{
                                        displayEmpty: true,
                                        MenuProps: {
                                            PaperProps: {
                                                sx: {
                                                    maxHeight: 300,
                                                    borderRadius: 2,
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {FEEDBACK_TYPES.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </CustomInput>
                            </Box>

                            {/* Feature Name */}
                            <Box>
                                <CustomInput
                                    select
                                    name="featureName"
                                    label="Feature / Module"
                                    size="small"
                                    value={formData.featureName}
                                    onChange={handleChange}
                                    onBlur={() => markTouched("featureName")}
                                    required
                                    fullWidth
                                    error={touched.featureName && Boolean(errors.featureName)}
                                    helperText={(touched.featureName && errors.featureName) || " "}
                                    SelectProps={{
                                        displayEmpty: true,
                                        MenuProps: {
                                            PaperProps: {
                                                sx: {
                                                    maxHeight: 300,
                                                    borderRadius: 2,
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {MODULE_FEATURES.map((feature) => (
                                        <MenuItem key={feature} value={feature}>
                                            {feature}
                                        </MenuItem>
                                    ))}
                                </CustomInput>
                            </Box>

                            {/* Comments */}
                            <Box sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
                                <CustomInput
                                    multiline
                                    required
                                    rows={5}
                                    name="comments"
                                    label="Describe your feedback"
                                    placeholder={`Please provide as much detail as possible.`}
                                    value={formData.comments}
                                    onChange={handleChange}
                                    onBlur={() => markTouched("comments")}
                                    error={touched.comments && !!errors.comments}
                                    helperText={
                                        (touched.comments && errors.comments) ||
                                        `${formData.comments.length}/2000 characters`
                                    }
                                    inputProps={{ maxLength: 2000 }}
                                    fullWidth
                                />
                            </Box>
                        </Box>

                        {/* Buttons */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 2,
                                pt: 2,
                                mt: 2,
                                borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <CustomButton
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={
                                    submitted ||
                                    loading ||
                                    !formData.feedbackType ||
                                    !formData.featureName ||
                                    !formData.comments.trim() ||
                                    Object.values(errors).some((v) => v)
                                }
                                sx={{
                                    height: 42,
                                    borderRadius: 3,
                                    fontWeight: 700,
                                    textTransform: "none",
                                }}
                                startIcon={loading ? <DotsLoader size={16} color="inherit" /> : null}
                            >
                                {loading ? "Submitting..." : "Submit Feedback"}
                            </CustomButton>
                        </Box>
                    </form>
                </CustomCard>
            </Box>

            {/* Snackbar */}
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.severity}
                onClose={handleCloseSnackbar}
                autoHideDuration={6000}
            />
        </Box>
    );
};

export default FeedbackPage;